import { createContext, useContext, type ReactNode } from 'react'

export type ProjectRole = 'admin' | 'data_entry' | 'approver' | 'viewer' | 'super_user' | null

/** The roles `project_role_enum` actually holds. 'super_user' is UI-only. */
export type DbProjectRole = Exclude<ProjectRole, 'super_user' | null>

/**
 * Split a UI role into the two columns the database stores.
 *
 * Super User is a platform-level role: it holds no project_memberships row, so
 * project_role_enum has no 'super_user' value. Writing the literal is a 22P02
 * error. The DB records a NULL role plus a super-user flag, the same way
 * audit_entries pairs actor_role_at_time with is_super_user_action.
 */
export function toDbRole(role: ProjectRole): {
  dbRole: DbProjectRole | null
  isSuperUser: boolean
} {
  if (role === 'super_user') return { dbRole: null, isSuperUser: true }
  return { dbRole: role, isSuperUser: false }
}

interface ProjectRoleContextValue {
  role: ProjectRole
  isLoading: boolean
  canEdit: boolean
  canApprove: boolean
  canManageMembers: boolean
  canManageForms: boolean
}

const ProjectRoleContext = createContext<ProjectRoleContextValue>({
  role: null,
  isLoading: true,
  canEdit: false,
  canApprove: false,
  canManageMembers: false,
  canManageForms: false,
})

export function derivePermissions(role: ProjectRole) {
  const isSU = role === 'super_user'
  const isAdmin = role === 'admin' || isSU
  const isApprover = role === 'approver' || isAdmin
  const isDataEntry = role === 'data_entry' || isAdmin
  return {
    canEdit: isDataEntry,
    canApprove: isApprover,
    canManageMembers: isAdmin,
    canManageForms: isAdmin,
  }
}

interface ProjectRoleProviderProps {
  role: ProjectRole
  isLoading: boolean
  children: ReactNode
}

export function ProjectRoleProvider({ role, isLoading, children }: ProjectRoleProviderProps) {
  const perms = derivePermissions(role)
  return (
    <ProjectRoleContext.Provider value={{ role, isLoading, ...perms }}>
      {children}
    </ProjectRoleContext.Provider>
  )
}

export function useProjectRole() {
  return useContext(ProjectRoleContext)
}
